import {
  type Duration,
  Context,
  Data,
  Match,
  Effect,
  Fiber,
  HashMap,
  Option,
  Ref,
  Scope,
  Semaphore,
} from 'effect';
import type * as Atom from 'effect/unstable/reactivity/Atom';
import type * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import * as AtomRegistry from 'effect/unstable/reactivity/AtomRegistry';

import type { ListingChange } from '../../capability/productListing/listingSelection';

export type ListingIntent = Data.TaggedEnum<{
  Start: { readonly change: ListingChange; readonly source: 'pointer' | 'focus' };
  Cancel: { readonly source: 'pointer' | 'focus' };
}>;

export const ListingIntent = Data.taggedEnum<ListingIntent>();

export class ListingPrefetch extends Context.Service<
  ListingPrefetch,
  {
    readonly intent: (command: ListingIntent) => Effect.Effect<void>;
  }
>()('@engine/ListingPrefetch') {}

/** Shares speculative candidate permits across one browser registry. */
export const makeListingPrefetch = (options: {
  readonly budget: Semaphore.Semaphore;
  readonly delay?: Duration.Input;
  readonly workScope: Scope.Scope;
  readonly prefetch: (change: ListingChange) => Effect.Effect<void>;
  readonly enabled: () => boolean;
}) =>
  Effect.gen(function* () {
    const scope = yield* Scope.Scope;
    const pending = yield* Ref.make(HashMap.empty<'pointer' | 'focus', Fiber.Fiber<void>>());
    const cancel = (source: 'pointer' | 'focus') =>
      Effect.gen(function* () {
        yield* Option.match(HashMap.get(yield* Ref.get(pending), source), {
          onNone: () => Effect.void,
          onSome: (fiber) => Fiber.interrupt(fiber).pipe(Effect.asVoid),
        });
        yield* Ref.update(pending, HashMap.remove(source));
      });
    return ListingPrefetch.of({
      intent: (command) =>
        Match.value(command).pipe(
          Match.tag('Cancel', ({ source }) => cancel(source)),
          Match.tag('Start', ({ source, change }) =>
            Effect.gen(function* () {
              yield* cancel(source);
              yield* Match.value(options.enabled()).pipe(
                Match.when(false, () => Effect.void),
                Match.when(true, () =>
                  Effect.gen(function* () {
                    const fiber = yield* Effect.forkIn(
                      Effect.sleep(
                        Option.getOrElse(Option.fromUndefinedOr(options.delay), () => '150 millis')
                      ).pipe(
                        Effect.andThen(
                          Effect.forkIn(
                            options
                              .prefetch(change)
                              .pipe(
                                Semaphore.withPermitsIfAvailable(options.budget, 1),
                                Effect.asVoid
                              ),
                            options.workScope
                          )
                        ),
                        Effect.asVoid
                      ),
                      scope
                    );
                    yield* Ref.update(pending, HashMap.set(source, fiber));
                  })
                ),
                Match.exhaustive
              );
            })
          ),
          Match.exhaustive
        ),
    });
  });

/** Reads a registry query until its current lookup finishes. */
export const awaitPrefetch = <A, E>(
  registry: AtomRegistry.AtomRegistry,
  atom: Atom.Atom<AsyncResult.AsyncResult<A, E>>
) =>
  Effect.acquireUseRelease(
    Effect.sync(() => registry.mount(atom)),
    () =>
      AtomRegistry.getResult(registry, atom, { suspendOnWaiting: true }).pipe(
        Effect.ignore,
        Effect.asVoid
      ),
    (release) => Effect.sync(release)
  );
